import classNames from "classnames";
import useForceUpdate from "PFCore/helpers/use_force_update";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useBooking } from "PFCore/hooks/queries";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { Booking } from "PFTypes";
import React, { useEffect, useState } from "react";

import { useBookingsOverviewContext } from "../../../context/bookings_overview_context";
import css from "../bars.module.scss";
import { BookingBarContainer } from "./booking_bar_container";
import { BookingBarData, BookingBarProvider } from "./booking_bar_provider";
import useBookingBarDnD from "./use_booking_bar_dnd";

export const BookingBar = (props: BookingBarData): JSX.Element => {
  const { splitMode } = useBookingsOverviewContext();
  const { utc } = useDateFormatter();
  const {
    onLoadingChange,
    onBarDragStart,
    onBarDragEnd,
    left,
    width,
    id,
    endDate: calendarEndDate,
    start,
    end,
    rowRef,
    disabled,
    roleBookingsIds
  } = props;
  const forceUpdate = useForceUpdate(); //to trigger update when Refs change
  const [touched, setTouched] = useState<boolean>(false);
  const [error, setError] = useState<string>();
  const { getFirstError } = useResponseErrors();

  const { data: booking, isLoading } = useBooking(id, {
    enabled: touched,
    onError: (err) => {
      setError(getFirstError(err));
    }
  });

  const {
    draggingMode,
    isUpdating,
    barDimensions,
    getMouseDownHandler,
    start: draggingStartDate,
    end: draggingEndDate
  } = useBookingBarDnD({
    start,
    end,
    booking: booking as Booking,
    left,
    width,
    calendarEndDate,
    onMouseDown: () => {
      onBarDragStart(id);
      forceUpdate();
    },
    onMouseMoveEnd: () => {
      forceUpdate();
    },
    onMouseUpEnd: ({ daysDiff }) => {
      !daysDiff && forceUpdate();
      onBarDragEnd();
    },
    rowRef
  });

  useEffect(() => {
    onLoadingChange(isUpdating);
  }, [isUpdating, onLoadingChange]);

  const { left: barLeft, width: barWidth } = barDimensions;
  const isEditable =
    !!booking && !booking.readonly && booking.source === "ui" && booking.booking_category_id != null; // eslint-disable-line eqeqeq
  const isSplittable = splitMode && isEditable && utc(end).diff(utc(start), "days", true) > 1;

  return (
    <>
      <BookingBarProvider
        bar={{ ...props, left: barLeft, width: barWidth }}
        booking={booking}
        isLoading={isLoading}
        isEditable={isEditable && !splitMode}
        isSplittable={isSplittable}
        draggingMode={draggingMode}
        draggingStartDate={draggingStartDate}
        draggingEndDate={draggingEndDate}
      >
        <BookingBarContainer
          onMouseDown={booking ? getMouseDownHandler("start_end") : undefined}
          onLeftEdgeMouseDown={booking ? getMouseDownHandler("start") : undefined}
          onRightEdgeMouseDown={booking ? getMouseDownHandler("end") : undefined}
          classes={{
            root: classNames(css.bookingBar, { [css.bookingBarGrabbing]: !!draggingMode })
          }}
          onHover={() => setTouched(true)}
          isDisabled={disabled}
          error={error}
          roleBookingsIds={roleBookingsIds}
        />
        {draggingMode === "start_end" && (
          <BookingBarContainer
            isDisabled
            classes={{
              pill: css.bookingBarPillClone
            }}
          />
        )}
      </BookingBarProvider>
    </>
  );
};
